import React from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Slider,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CallIcon from "@mui/icons-material/Call";
import MicOff from "@mui/icons-material/MicOff";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MicIcon from "@mui/icons-material/Mic";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeDownRoundedIcon from "@mui/icons-material/VolumeDownRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import controls, {
  answerCall,
  endCall,
  prepareAgentMicrophone,
  publishAgentMicrophone,
  queueBack,
  resumeAgentAudio,
  unlockAgentAudioPlayback,
} from "./controls";
import {showSnackbar} from "./Snackbar";
import {elapsedCallSeconds, formatCallTime} from "./callTime";

const getSignalTone = (quality) => {
  if (quality === "lost" || quality === "poor") return {label: "Poor", color: "error"};
  if (quality === "good") return {label: "Good", color: "success"};
  if (quality === "excellent") return {label: "Excellent", color: "success"};
  return {label: "Checking", color: "default"};
};

function CallerTile(props) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [pendingAction, setPendingAction] = React.useState("");
  const [confirmEndArmed, setConfirmEndArmed] = React.useState(false);
  const endConfirmTimerRef = React.useRef(null);
  const tileRef = React.useRef(null);
  const status = props.callStatus || {};
  const time = props.time || 0;
  const micOn = Boolean(props.micOn);
  const isActiveCall = status.activeCallerId === props.id;
  const activeCallExists = Boolean(status.activeCallerId);
  const callAnsweredBySomeOneElse = props.answeredBy.length > 2 && props.answeredBy !== controls.channelName;
  const isWaiting = !String(props.answeredBy || "").trim();
  const monitorState = props.reviewerMonitoring?.getState(props) || {};
  const waitingSeconds = isWaiting ?
    elapsedCallSeconds(props.timeRing, props.now) : 0;
  const waitingLabel = formatCallTime(waitingSeconds).replace(/^00:/, "");
  const waitingTone = waitingSeconds >= 120 ? "error" :
    waitingSeconds >= 30 ? "warning" : "default";
  const otherCallDuration = formatCallTime(
      elapsedCallSeconds(props.timeAnswered, props.now),
  );
  const callerSpeaking = isActiveCall && !status.callerMuted &&
    (status.callerSpeaking || status.callerLevel > 0.08);
  const agentSpeaking = isActiveCall && micOn &&
    (status.agentSpeaking || status.agentLevel > 0.08);
  const callerSignal = getSignalTone(status.callerNetworkQuality);
  const agentSignal = status.phase === "reconnecting" ?
    {label: "Reconnecting", color: "warning"} : getSignalTone(status.agentNetworkQuality);
  const open = Boolean(anchorEl);
  const popoverId = open ? "more-menu" : undefined;
  const personIconSize = callAnsweredBySomeOneElse ? 38 : (isActiveCall ? 27 : 88);
  const actionButtonWidth = isActiveCall ? 46 : 50;
  const actionButtonHeight = isActiveCall ? 38 : 42;
  const recordingNeedsAttention = isActiveCall && status.recordingStatus === "problem";
  const durationTone = time >= 20 * 60 ? "error" :
    time >= 15 * 60 ? "warning" : "primary";
  const durationLabel = `${("0" + Math.floor(time / 3600)).slice(-2)}:${("0" + Math.floor(time / 60)).slice(-2)}:${("0" + time % 60).slice(-2)}`;
  const durationTooltip = time >= 20 * 60 ?
    "This call has been connected for more than 20 minutes." :
    time >= 15 * 60 ?
      "This call is approaching the long-call review point." :
      "Current connected call duration.";
  const callerId = String(props.id || "");
  const anonymousCaller = !callerId || callerId.includes("null");

  const callerPhase = (() => {
    if (!isActiveCall) return {label: "Waiting", color: "default"};
    if (status.phase === "assigning") return {label: "Assigning call", color: "default"};
    if (status.phase === "connecting") return {label: "Connecting", color: "default"};
    if (status.phase === "reconnecting") return {label: "Reconnecting", color: "warning"};
    if (status.phase === "failed") return {label: "Connection failed", color: "error"};
    if (!status.callerConnected || !status.callerAudioReady || status.phase === "waiting-caller") {
      return {label: "Waiting for caller audio", color: "default"};
    }
    if (status.callerMuted) return {label: "Caller muted", color: "warning"};
    if (callerSpeaking) return {label: "Caller speaking", color: "success"};
    return {label: "Caller audio ready", color: "default"};
  })();

  const networkTooltip = !isActiveCall ? "Answer call" :
    (agentSignal.color === "success" ? "Your network is stable" :
      agentSignal.color === "error" ? "Your network is poor. Switch to stronger Wi-Fi or mobile data." :
        status.phase === "reconnecting" ? "Reconnecting. Keep the app open and avoid switching tabs." :
          "Checking your network quality.");

  React.useEffect(() => {
    if (isActiveCall) tileRef.current?.scrollIntoView?.({block: "nearest", inline: "nearest"});
  }, [isActiveCall]);

  React.useEffect(() => () => {
    if (endConfirmTimerRef.current) clearTimeout(endConfirmTimerRef.current);
  }, []);

  React.useEffect(() => {
    if (!isActiveCall) return;
    if (status.phase === "reconnecting") {
      showSnackbar("Reconnecting... keep this call page active.", "warning");
    } else if (status.agentNetworkQuality === "poor" || status.agentNetworkQuality === "lost") {
      showSnackbar("Your network is poor. Move closer to strong Wi-Fi or switch data.", "warning");
    }
  }, [isActiveCall, status.phase, status.agentNetworkQuality]);

  const handleMicToggle = async () => {
    if (pendingAction || !isActiveCall) return;
    if (status.micPermission !== "ready") {
      const ready = await prepareAgentMicrophone();
      props.setMicOn(controls.micOn);
      if (!ready) {
        showSnackbar("Allow microphone access in your browser settings.", "warning");
        return;
      }
    }

    const nextMicState = !controls.micOn;
    try {
      controls.micOn = nextMicState;
      if (nextMicState) {
        if (!await publishAgentMicrophone()) {
          controls.micOn = false;
          props.setMicOn(false);
          showSnackbar(
              "Your microphone is not sending audio. Open Audio and choose another microphone.",
              "warning",
          );
          return;
        }
      } else {
        await controls.room?.localParticipant?.setMicrophoneEnabled(false);
        controls.microphoneTrack = null;
      }
      props.setMicOn(nextMicState);
    } catch (_) {
      controls.micOn = false;
      controls.microphonePermission = "denied";
      props.setMicOn(false);
      showSnackbar("Microphone access is blocked.", "warning");
    }
  };

  const handleAnswer = async (resume = false) => {
    if (pendingAction) return;
    setPendingAction(resume ? "retry" : "answer");
    try {
      const audioUnlock = unlockAgentAudioPlayback();
      await answerCall(props.id, props.setTime, resume);
      await audioUnlock;
      props.setMicOn(controls.micOn);
    } finally {
      setPendingAction("");
    }
  };

  const handleEnd = async () => {
    if (pendingAction) return;
    if (!confirmEndArmed) {
      setConfirmEndArmed(true);
      showSnackbar("Press end again to confirm.", "warning");
      clearTimeout(endConfirmTimerRef.current);
      endConfirmTimerRef.current = setTimeout(() => setConfirmEndArmed(false), 2200);
      return;
    }

    clearTimeout(endConfirmTimerRef.current);
    setConfirmEndArmed(false);
    setPendingAction("end");
    try {
      await endCall(props.setMicOn, props.setTime, props.id);
    } finally {
      setPendingAction("");
    }
  };

  const copyCallerId = async (event) => {
    event?.stopPropagation?.();
    if (anonymousCaller) {
      showSnackbar("Anonymous caller.", "warning");
      return;
    }
    try {
      await navigator.clipboard.writeText(callerId);
      showSnackbar("Caller ID copied.");
    } catch (_) {
      showSnackbar("Caller ID could not be copied.", "warning");
    }
  };

  const sendToAnotherRoom = (room) => {
    setAnchorEl(null);
    queueBack(props.id, room.roomId, room.name, props.setTime, props.setMicOn);
  };

  const recovery = isActiveCall && (
    status.phase === "failed" ||
    ["denied", "unavailable"].includes(status.micPermission) ||
    status.audioBlocked
  );

  return (
    <Box
      ref={tileRef}
      position="relative"
      sx={{
        mx: 0.5,
        flex: "0 0 auto",
        width: {xs: "100%", sm: 158, md: 166, lg: 174, xl: 182},
        scrollSnapAlign: "start",
        opacity: activeCallExists && !isActiveCall ? 0.66 : 1,
        transition: "opacity 160ms ease",
      }}
      onClick={() => props.onSelect?.(props.id)}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          height: {xs: 270, sm: 300},
          borderRadius: "10px",
          border: "1px solid",
          borderColor: props.selected || isActiveCall ? "primary.main" : "divider",
          backgroundColor: (theme) => isActiveCall ?
            (theme.palette.mode === "dark" ? "#19302a" : "#f1f9f6") :
            (theme.palette.mode === "dark" ? "#151d1b" : "#fff"),
          boxShadow: props.selected ? "0 0 0 2px rgba(0,95,80,0.12)" : "none",
          animation: props.isHighlighted ? "callerPulse 1.2s ease" : "none",
          "@keyframes callerPulse": {
            "0%": {boxShadow: "0 0 0 0 rgba(0,95,80,0.35)"},
            "100%": {boxShadow: "0 0 0 10px rgba(0,95,80,0)"},
          },
        }}
      >
        <Stack alignItems="center" sx={{px: 1, pb: 1.2, height: "100%"}}>
          <Stack direction="row" alignItems="center" sx={{width: "100%", mt: 0.8}}>
            <Chip label={`${props.queueNumber || 1}`} size="small" color={isActiveCall ? "primary" : "default"} sx={{fontWeight: 700}} />
            <Box sx={{flex: 1}} />
            {isWaiting && Number(props.timeRing || 0) > 0 && (
              <Tooltip title={`Waiting for ${waitingLabel}`}>
                <Chip
                  size="small"
                  icon={<ScheduleRoundedIcon />}
                  label={waitingLabel}
                  color={waitingTone}
                  aria-label={`Waiting time ${waitingLabel}`}
                  sx={{
                    height: 23,
                    mx: 0.15,
                    fontVariantNumeric: "tabular-nums",
                    "& .MuiChip-icon": {fontSize: 13, ml: 0.55},
                    "& .MuiChip-label": {
                      px: 0.55,
                      fontSize: "0.64rem",
                      fontWeight: 700,
                    },
                  }}
                />
              </Tooltip>
            )}
            {isActiveCall && status.recordingStatus === "recording" && (
              <Tooltip title="Recording active">
                <FiberManualRecordRoundedIcon color="error" sx={{fontSize: 13}} />
              </Tooltip>
            )}
            <Tooltip title="More options">
              <span>
                <IconButton disabled={callAnsweredBySomeOneElse} onClick={(event) => setAnchorEl(event.currentTarget)} aria-describedby={popoverId} size="small">
                  <MoreHorizIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Popover
            id={popoverId}
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            transformOrigin={{vertical: "top", horizontal: "right"}}
          >
            <ButtonGroup variant="contained" orientation="vertical" size="small" color="inherit">
              <Button disabled={props.id.toString().includes("null")} onClick={copyCallerId}>Copy caller ID</Button>
              <Button onClick={() => sendToAnotherRoom({name: controls.channelName, roomId: controls.roomId})} color="warning">Queue back</Button>
              {controls.rooms.filter((room) => room.roomId !== controls.roomId).map((room) => (
                <Button key={room.roomId} onClick={() => sendToAnotherRoom(room)} color="warning">Send to {room.name}</Button>
              ))}
            </ButtonGroup>
          </Popover>

          <Tooltip
            arrow
            title={anonymousCaller ?
              "Anonymous caller" :
              `Caller ID: ${callerId}. Click to copy.`}
          >
            <IconButton
              size="small"
              onClick={copyCallerId}
              aria-label={anonymousCaller ?
                `Anonymous caller in queue ${props.queueNumber || 1}` :
                `Copy caller ID for queue ${props.queueNumber || 1}`}
              sx={{
                p: 0,
                mt: isActiveCall ? 0 : 0.7,
                mb: isActiveCall ? 0 : 0.45,
                color: isActiveCall ? "primary.main" : "text.primary",
              }}
            >
              <PersonIcon sx={{fontSize: personIconSize}} />
            </IconButton>
          </Tooltip>

          <Box sx={{height: isActiveCall ? 38 : callAnsweredBySomeOneElse ? 64 : 48, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", mb: 0.35}}>
            {isActiveCall && (
              <Tooltip title={durationTooltip}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.55}
                  aria-label={`Live call duration ${durationLabel}`}
                  sx={{
                    px: 1.05,
                    py: 0.35,
                    borderRadius: "999px",
                    color: `${durationTone}.main`,
                    bgcolor: (theme) => alpha(
                        theme.palette[durationTone].main,
                        theme.palette.mode === "dark" ? 0.18 : 0.09,
                    ),
                    border: "1px solid",
                    borderColor: `${durationTone}.main`,
                  }}
                >
                  <FiberManualRecordRoundedIcon
                    color={durationTone}
                    sx={{fontSize: 9}}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.54rem",
                      fontWeight: 750,
                      letterSpacing: 0.7,
                    }}
                  >
                    LIVE
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 750,
                      letterSpacing: 0.15,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {durationLabel}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
            {callAnsweredBySomeOneElse && (
              <Stack alignItems="center" spacing={0.45} sx={{minWidth: 0, width: "100%"}}>
                <Chip color="success" icon={<CallIcon />} label={props.answeredBy} size="small" sx={{maxWidth: "100%", fontWeight: 600}} />
                <Typography
                  variant="caption"
                  color="success.main"
                  aria-label={`Live call duration ${otherCallDuration}`}
                  sx={{fontWeight: 700, fontVariantNumeric: "tabular-nums"}}
                >
                  Live · {otherCallDuration}
                </Typography>
              </Stack>
            )}
            {!isActiveCall && !callAnsweredBySomeOneElse && (
              <Tooltip title={anonymousCaller ? "Anonymous caller" : callerId}>
                <Stack
                  alignItems="center"
                  spacing={0.15}
                  onClick={copyCallerId}
                  sx={{
                    minWidth: 0,
                    maxWidth: "100%",
                    px: 0.6,
                    cursor: anonymousCaller ? "default" : "pointer",
                  }}
                >
                  <Typography
                    component="span"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.53rem",
                      fontWeight: 700,
                      letterSpacing: 0.75,
                      textTransform: "uppercase",
                    }}
                  >
                    Caller ID
                  </Typography>
                  <Typography
                    component="span"
                    color="text.secondary"
                    noWrap
                    sx={{maxWidth: "100%", fontSize: "0.65rem"}}
                  >
                    {anonymousCaller ? "Anonymous caller" : callerId}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Box>

          {isActiveCall && !recovery && (
            <Stack spacing={0.45} sx={{width: "100%", alignItems: "center"}} aria-live="polite">
              <Chip
                size="small"
                icon={<GraphicEqRoundedIcon sx={{fontSize: 13}} />}
                label={callerPhase.label}
                color={callerPhase.color}
                sx={{height: 22, maxWidth: "100%", "& .MuiChip-label": {px: 0.7, fontSize: "0.64rem", fontWeight: 600}}}
              />
              <Chip
                size="small"
                icon={<WifiRoundedIcon sx={{fontSize: 13}} />}
                label={`Caller: ${callerSignal.label}`}
                color={callerSignal.color}
                sx={{height: 22, "& .MuiChip-label": {px: 0.7, fontSize: "0.64rem", fontWeight: 600}}}
              />
              {recordingNeedsAttention && (
                <Chip size="small" color="warning" label="Recording needs review" sx={{height: 22, "& .MuiChip-label": {fontSize: "0.62rem", fontWeight: 600}}} />
              )}
            </Stack>
          )}

          {isActiveCall && (
            <Tooltip
              title={`Call speaker volume ${Math.round(
                  (props.callVolume ?? 1) * 100,
              )}%`}
            >
              <Stack
                direction="row"
                spacing={0.45}
                alignItems="center"
                onClick={(event) => event.stopPropagation()}
                sx={{width: "100%", px: 0.4, mt: 0.35}}
              >
                <VolumeDownRoundedIcon
                  color="primary"
                  sx={{fontSize: 15}}
                />
                <Slider
                  size="small"
                  min={0}
                  max={1}
                  step={0.05}
                  value={props.callVolume ?? 1}
                  onChange={(_, value) => props.setCallVolume?.(value)}
                  aria-label="Call speaker volume"
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                  sx={{py: 0.4}}
                />
                <VolumeUpRoundedIcon
                  color="primary"
                  sx={{fontSize: 15}}
                />
              </Stack>
            </Tooltip>
          )}

          {recovery && (
            <Paper elevation={0} role="alert" sx={{width: "100%", p: 0.7, border: "1px solid", borderColor: "warning.main", borderRadius: "8px", bgcolor: "transparent"}}>
              <Stack direction="row" spacing={0.55} alignItems="center">
                {status.audioBlocked ? <VolumeUpRoundedIcon color="warning" sx={{fontSize: 17}} /> : <ErrorOutlineRoundedIcon color="warning" sx={{fontSize: 17}} />}
                <Box sx={{minWidth: 0, flex: 1}}>
                  <Typography sx={{fontSize: "0.67rem", fontWeight: 650, lineHeight: 1.15}}>
                    {status.phase === "failed" ?
                      "Connection failed" :
                      status.micPermission === "denied" ?
                        "Microphone blocked" :
                        status.micPermission === "unavailable" ?
                          "Microphone is not sending" :
                          "Caller audio paused"}
                  </Typography>
                  <Typography color="text.secondary" sx={{fontSize: "0.57rem", lineHeight: 1.2}}>
                    {status.phase === "failed" ? "Check your network and retry." : status.micPermission === "denied" ? "Allow microphone access." : status.micPermission === "unavailable" ? "Open Audio and choose another microphone." : "Resume browser audio."}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={Boolean(pendingAction)}
                  onClick={async () => {
                    if (status.phase === "failed") return handleAnswer(true);
                    if (["denied", "unavailable"].includes(status.micPermission)) {
                      await prepareAgentMicrophone();
                      if (controls.micOn) await publishAgentMicrophone();
                      props.setMicOn(Boolean(controls.micOn && controls.microphoneTrack));
                      return;
                    }
                    await resumeAgentAudio();
                  }}
                  sx={{minWidth: 0, px: 0.7, fontSize: "0.6rem"}}
                >
                  {pendingAction === "retry" ? <CircularProgress size={13} color="inherit" /> : status.phase === "failed" ? "Retry" : ["denied", "unavailable"].includes(status.micPermission) ? "Check" : "Resume"}
                </Button>
              </Stack>
            </Paper>
          )}

          {callAnsweredBySomeOneElse && monitorState.available ? <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={monitorState.joining}
            startIcon={monitorState.joining ?
              <CircularProgress size={15} color="inherit" /> :
              <HeadphonesRoundedIcon />}
            onClick={(event) => {
              event.stopPropagation();
              props.reviewerMonitoring.listen(props);
            }}
            sx={{
              mt: "auto",
              minHeight: 42,
              borderRadius: "8px",
              fontSize: ".72rem",
              fontWeight: 750,
            }}
          >
            {monitorState.joining ? "Connecting…" : "Listen to call"}
          </Button> : <Stack direction="row" spacing={0.5} sx={{mt: "auto", width: "100%", justifyContent: "center", pb: 0.2}}>
            <Tooltip title={confirmEndArmed ? "Press again to end" : "End call (E)"}>
              <span>
                <Button
                  disabled={callAnsweredBySomeOneElse || Boolean(pendingAction) || (activeCallExists && !isActiveCall)}
                  onClick={handleEnd}
                  variant="outlined"
                  aria-label="End Call"
                  data-role="end-call"
                  data-caller-id={props.id}
                  color="error"
                  sx={{minWidth: 0, width: actionButtonWidth, height: actionButtonHeight, px: 0, bgcolor: confirmEndArmed ? "rgba(220,38,38,0.1)" : undefined}}
                >
                  {pendingAction === "end" ? <CircularProgress size={16} color="inherit" /> : <CallEndIcon />}
                </Button>
              </span>
            </Tooltip>

            <Tooltip title={micOn ? "Mute microphone (M)" : "Unmute microphone (M)"}>
              <span>
                <Button
                  disabled={!isActiveCall || Boolean(pendingAction)}
                  onClick={handleMicToggle}
                  variant="outlined"
                  aria-label="Mic Toggle"
                  data-role="mic-toggle"
                  data-caller-id={props.id}
                  color={micOn ? "primary" : "warning"}
                  sx={{
                    minWidth: 0,
                    width: actionButtonWidth,
                    height: actionButtonHeight,
                    px: 0,
                    bgcolor: agentSpeaking ? "rgba(22,163,74,0.1)" : undefined,
                    boxShadow: agentSpeaking ? `0 0 0 3px rgba(22,163,74,${Math.min(0.28, status.agentLevel * 0.35)})` : "none",
                    transition: "box-shadow 120ms linear, background-color 120ms ease",
                  }}
                >
                  {!micOn ? <MicOff /> : agentSpeaking ? <GraphicEqRoundedIcon /> : <MicIcon />}
                </Button>
              </span>
            </Tooltip>

            <Tooltip title={networkTooltip}>
              <span>
                <Button
                  onClick={() => isActiveCall ? (status.phase === "failed" ? handleAnswer(true) : undefined) : handleAnswer(false)}
                  disabled={callAnsweredBySomeOneElse || Boolean(pendingAction) || (!isActiveCall && activeCallExists)}
                  variant="outlined"
                  aria-label={isActiveCall ? "Agent network status" : "Answer Call"}
                  data-role="answer-call"
                  data-caller-id={props.id}
                  color={isActiveCall ? (agentSignal.color === "default" ? "primary" : agentSignal.color) : "success"}
                  sx={{minWidth: 0, width: actionButtonWidth, height: actionButtonHeight, px: 0}}
                >
                  {pendingAction === "answer" || pendingAction === "retry" ? <CircularProgress size={16} color="inherit" /> : isActiveCall ? <WifiRoundedIcon /> : <CallIcon />}
                </Button>
              </span>
            </Tooltip>
          </Stack>}
        </Stack>
      </Paper>
    </Box>
  );
}

export default React.memo(CallerTile);
